// src/components/WeightChart.tsx — dependency-free inline SVG line chart.
interface Point {
  date: string;
  value: number;
}

export function WeightChart({ points }: { points: Point[] }) {
  if (points.length === 0) {
    return <p className="text-sm text-ls-muted">No data to chart.</p>;
  }
  if (points.length === 1) {
    const p = points[0]!;
    return (
      <p className="text-sm text-ls-fg">
        {p.value} lbs on {p.date}
      </p>
    );
  }

  const w = 600;
  const h = 160;
  const pad = 24;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const coords = points.map((p, i) => {
    const x = pad + (i / (points.length - 1)) * (w - 2 * pad);
    const y = h - pad - ((p.value - min) / span) * (h - 2 * pad);
    return { x, y, ...p };
  });

  const path = coords
    .map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="h-40 w-full"
      role="img"
      aria-label="Weight over time"
      preserveAspectRatio="none"
    >
      <path d={path} fill="none" stroke="hsl(var(--ls-accent))" strokeWidth={2} />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={2.5} fill="hsl(var(--ls-accent))" />
      ))}
    </svg>
  );
}
